(() => {
  let isLoading = false;
  const SECTION_CACHE_TTL_MS = 5 * 60 * 1000;
  const sectionHtmlCache = new Map();
  const sectionFetchInflight = new Map();
  const complementaryHtmlCache = new Map();
  const complementaryFetchInflight = new Map();

  const getMainProductSection = () =>
    document.querySelector('.shopify-section--main-product') ||
    document.querySelector('#main-product-section')?.closest('.shopify-section');

  const getEtUpgradeSection = () => document.querySelector('.shopify-section.et-upgrade');

  const getFeaturedCollectionSection = () =>
    document.querySelector('.shopify-section.ecom-feat-coll');

  const getIncludedItemsSection = () => {
    const anchor =
      document.querySelector('.included-items-container') || document.querySelector('#included-items');
    return anchor?.closest('.shopify-section') || null;
  };

  const setIncludedItemsDrawerDirection = (drawer) => {
    if (!drawer) return;
    drawer.setAttribute('open-from', window.innerWidth <= 699 ? 'bottom' : 'right');
  };

  // The featured-collection slider is normally initialised by an inline
  // DOMContentLoaded script, which never re-runs after a section swap, so we
  // re-create the Swiper here (mirroring sections/ecom-featured-collection.liquid).
  const initFeaturedCollectionSwiper = (sectionEl) => {
    if (typeof Swiper === 'undefined' || !sectionEl) return;

    const swiperEl = sectionEl.querySelector('.ecom-feat-coll__swiper');
    if (!swiperEl) return;

    if (swiperEl.swiper) swiperEl.swiper.destroy(true, true);

    new Swiper(swiperEl, {
      slidesPerView: 1.3,
      spaceBetween: 20,
      grabCursor: true,
      watchOverflow: true,
      scrollbar: {
        el: swiperEl.querySelector('.ecom-feat-coll__swiper-scrollbar'),
        draggable: true,
        hide: false,
      },
      mousewheel: { forceToAxis: true },
      navigation: {
        nextEl: swiperEl.querySelector('.ecom-feat-coll__next'),
        prevEl: swiperEl.querySelector('.ecom-feat-coll__prev'),
      },
      breakpoints: {
        699: { slidesPerView: 2, spaceBetween: 20 },
        1000: { slidesPerView: 3, spaceBetween: 40 },
      },
    });
  };

  const getViewSuffix = () => {
    try {
      const view = new URLSearchParams(window.location.search).get('view');
      return view && String(view).trim() ? String(view).trim() : null;
    } catch {
      return null;
    }
  };

  const appendViewToHandleAttr = (handleAttr, viewSuffix) => {
    if (!handleAttr || !viewSuffix) return handleAttr;
    if (String(handleAttr).includes('view=')) return handleAttr;

    const [pathPart, queryPart] = String(handleAttr).split('?');
    const params = new URLSearchParams(queryPart || '');
    params.set('view', viewSuffix);
    const qs = params.toString();
    return qs ? `${pathPart}?${qs}` : pathPart;
  };

  const parseSectionFromResponse = (html, sectionEl, sectionId) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    let parsed =
      (sectionEl?.id && doc.getElementById(sectionEl.id)) ||
      doc.getElementById(`shopify-section-${sectionId}`) ||
      doc.querySelector('.shopify-section--main-product') ||
      doc.querySelector('.shopify-section.et-upgrade');

    if (!parsed) {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html.trim();
      parsed =
        (sectionEl?.id && wrapper.querySelector(`#${sectionEl.id}`)) ||
        wrapper.querySelector(`#shopify-section-${sectionId}`) ||
        wrapper.firstElementChild;
    }

    return parsed;
  };

  const getSectionCacheKey = (productUrl, sectionId) => {
    const fetchUrl = new URL(productUrl, window.location.origin);
    const view = getViewSuffix();
    if (view) fetchUrl.searchParams.set('view', view);
    return `${fetchUrl.pathname}|${sectionId}`;
  };

  const fetchSectionHtml = async (productUrl, sectionId, viewSuffix, priority = 'auto') => {
    const fetchUrl = new URL(productUrl, window.location.origin);
    fetchUrl.searchParams.set('section_id', sectionId);
    const view = viewSuffix || getViewSuffix();
    if (view) fetchUrl.searchParams.set('view', view);

    const response = await fetch(fetchUrl.toString(), { priority });
    if (!response.ok) throw new Error(`Section fetch failed (${sectionId}): ${response.status}`);

    return response.text();
  };

  const fetchSectionHtmlCached = async (productUrl, sectionId, priority = 'auto') => {
    const cacheKey = getSectionCacheKey(productUrl, sectionId);
    const cached = sectionHtmlCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < SECTION_CACHE_TTL_MS) {
      return cached.html;
    }

    if (sectionFetchInflight.has(cacheKey)) {
      return sectionFetchInflight.get(cacheKey);
    }

    const promise = fetchSectionHtml(productUrl, sectionId, null, priority)
      .then((html) => {
        sectionHtmlCache.set(cacheKey, { html, ts: Date.now() });
        sectionFetchInflight.delete(cacheKey);
        return html;
      })
      .catch((error) => {
        sectionFetchInflight.delete(cacheKey);
        throw error;
      });

    sectionFetchInflight.set(cacheKey, promise);
    return promise;
  };

  const getComplementaryCacheKey = (productId, sectionId, limit) =>
    `${productId}|${sectionId}|${limit}`;

  const fetchComplementaryHtmlCached = async (productId, sectionId, limit) => {
    const cacheKey = getComplementaryCacheKey(productId, sectionId, limit);
    const cached = complementaryHtmlCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < SECTION_CACHE_TTL_MS) {
      return cached.html;
    }

    if (complementaryFetchInflight.has(cacheKey)) {
      return complementaryFetchInflight.get(cacheKey);
    }

    const promise = fetchComplementaryHtml(productId, sectionId, limit)
      .then((html) => {
        complementaryHtmlCache.set(cacheKey, { html, ts: Date.now() });
        complementaryFetchInflight.delete(cacheKey);
        return html;
      })
      .catch((error) => {
        complementaryFetchInflight.delete(cacheKey);
        throw error;
      });

    complementaryFetchInflight.set(cacheKey, promise);
    return promise;
  };

  const getPdpSwapRoot = (el) => el?.closest('[data-pdp-color-swap], [data-pdp-set-swap]');

  // Synthetic events (bots, in-app browsers) can target document or a text node, which have no closest().
  const getPdpSwatch = (el) =>
    el instanceof Element ? el.closest('[data-pdp-color-swatch], [data-pdp-set-swatch]') : null;

  const getCurrentPdpHandle = () =>
    document.querySelector('[data-pdp-color-swap]')?.getAttribute('data-current-handle') ||
    document.querySelector('[data-pdp-set-swap]')?.getAttribute('data-current-handle');

  const prefetchSwapData = (swatch) => {
    if (!swatch || swatch.closest('quick-buy-drawer')) return;

    const root = getPdpSwapRoot(swatch);
    const handle = swatch.getAttribute('data-pdp-handle');
    const productUrl = swatch.getAttribute('data-pdp-url') || swatch.getAttribute('href');
    const productId = swatch.getAttribute('data-pdp-product-id');
    const currentHandle = root?.getAttribute('data-current-handle');

    if (!handle || !productUrl || handle === currentHandle) return;

    const mainSection = getMainProductSection();
    if (!mainSection) return;

    const mainSectionId = mainSection.id.replace('shopify-section-', '');
    fetchSectionHtmlCached(productUrl, mainSectionId, 'low').catch(() => {});

    if (hasComplementaryBlock(mainSection) && productId) {
      fetchComplementaryHtmlCached(
        productId,
        mainSectionId,
        getComplementaryLimit(mainSection)
      ).catch(() => {});
    }
  };

  const isComplementaryRecommendations = (el) =>
    (el?.getAttribute('intent') || 'complementary') === 'complementary';

  const hasComplementaryBlock = (sectionEl) => {
    if (!sectionEl) return false;
    return Boolean(
      sectionEl.querySelector('[data-block-type="associated-products"]') ||
      sectionEl.querySelector('[data-complementary-injected]') ||
      sectionEl.querySelector('product-recommendations')
    );
  };

  const getComplementaryLimit = (sectionEl, newSectionEl) => {
    for (const root of [sectionEl, newSectionEl].filter(Boolean)) {
      const recommendationsEl = root.querySelector('product-recommendations');
      if (recommendationsEl?.getAttribute('limit')) {
        return recommendationsEl.getAttribute('limit');
      }
      const injectedEl = root.querySelector('[data-complementary-injected]');
      if (injectedEl?.getAttribute('data-complementary-limit')) {
        return injectedEl.getAttribute('data-complementary-limit');
      }
    }
    return '4';
  };

  const getComplementaryInjectionSlots = (sectionEl) =>
    Array.from(sectionEl?.querySelectorAll('product-recommendations') || []).filter(
      isComplementaryRecommendations
    );

  const fetchProductIdByHandle = async (handle) => {
    if (!handle) return null;
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}products/${handle}.js`);
    if (!response.ok) return null;
    const data = await response.json();
    return data?.id ? String(data.id) : null;
  };

  const fetchComplementaryHtml = async (productId, sectionId, limit) => {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}recommendations/products`, window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('intent', 'complementary');
    url.searchParams.set('limit', limit);

    const response = await fetch(url.toString(), { priority: 'low' });
    if (!response.ok) throw new Error(`Complementary fetch failed: ${response.status}`);
    return response.text();
  };

  const extractComplementaryMarkup = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const candidates = Array.from(doc.querySelectorAll('product-recommendations'));

    const complementary = candidates.find(
      (el) => isComplementaryRecommendations(el) && el.childElementCount > 0
    );
    if (complementary) return complementary;

    return candidates.find((el) => el.childElementCount > 0) || null;
  };

  const injectComplementaryIntoSection = (sectionEl, sourceEl, limit = '4') => {
    const slots = getComplementaryInjectionSlots(sectionEl);
    if (!slots.length) return false;

    const templateChildren = sourceEl
      ? Array.from(sourceEl.childNodes).map((node) => node.cloneNode(true))
      : [];

    slots.forEach((placeholder) => {
      if (!templateChildren.length) {
        placeholder.remove();
        return;
      }

      const container = document.createElement('div');
      container.className = placeholder.className || 'block';
      container.setAttribute('data-complementary-injected', '');
      container.setAttribute('data-complementary-limit', placeholder.getAttribute('limit') || limit);
      if (placeholder.getAttribute('product')) {
        container.setAttribute('data-complementary-product-id', placeholder.getAttribute('product'));
      }
      container.append(...templateChildren.map((node) => node.cloneNode(true)));
      placeholder.replaceWith(container);
    });

    return templateChildren.length > 0;
  };

  const reinitProductRecommendations = (root = document) => {
    root.querySelectorAll('product-recommendations').forEach((el) => {
      const fresh = document.createElement('product-recommendations');
      Array.from(el.attributes).forEach((attr) => {
        fresh.setAttribute(attr.name, attr.value);
      });
      el.replaceWith(fresh);
    });
  };

  const initPdpStickyBar = (root = document) => {
    const scope =
      (root instanceof Element && root.matches?.('.shopify-section--main-product') && root) ||
      root.querySelector?.('.shopify-section--main-product') ||
      root.closest?.('.shopify-section--main-product') ||
      document.querySelector('.shopify-section--main-product');

    if (!scope) return;

    const setSwap = scope.querySelector('[data-pdp-set-swap]');
    const stickyValue = scope.querySelector('[data-sticky-set-value]');
    const selectorValue = setSwap?.querySelector('.gratis-pannen-selector__label-value');

    if (stickyValue && selectorValue) {
      stickyValue.textContent = selectorValue.textContent.trim();
    }

    const mainInventory = scope.querySelector(
      '.product-info [data-block-type="custom-inventory"] .product-info__custom-inventory, .product-info form .product-info__custom-inventory--preorder'
    );
    const stickyDesktop = scope.querySelector('[data-sticky-inventory-desktop]');

    if (mainInventory && stickyDesktop && !mainInventory.closest('[data-sticky-inventory]')) {
      const desktopItem = stickyDesktop.querySelector('.product-info__custom-inventory');
      const clone = mainInventory.cloneNode(true);

      if (desktopItem) {
        desktopItem.replaceWith(clone);
      } else {
        stickyDesktop.appendChild(clone);
      }
    }

    const mobileSlot = scope.querySelector('[data-sticky-inventory-mobile][data-inventory-state="preorder"]');
    const mainPreorderText = scope.querySelector(
      '.product-info form .product-info__custom-inventory--preorder .text-sm'
    );
    const mobileText = mobileSlot?.querySelector('.text-sm');

    if (mobileSlot && mobileText && mainPreorderText) {
      const dateMatch = mainPreorderText.textContent.match(/\d{1,2}\s+\w{3}\s+\d{4}/);
      const template = mobileSlot.dataset.preorderTemplate || '';
      const fallback = mobileSlot.dataset.preorderFallback || '';

      if (dateMatch && template.includes('[date]')) {
        mobileText.textContent = template.replace('[date]', dateMatch[0]);
      } else if (!dateMatch && fallback) {
        mobileText.textContent = fallback;
      }
    }
  };

  window.initPdpStickyBar = initPdpStickyBar;
  window.initGratisPannenStickyCta = initPdpStickyBar;

  const initIncludedItemsSectionSlider = (sectionEl) => {
    if (!sectionEl || typeof Swiper === 'undefined') return;

    const slider = sectionEl.querySelector('.included-items-slider');
    if (!slider) return;

    if (slider.swiper) slider.swiper.destroy(true, true);

    new Swiper(slider, {
      slidesPerView: 'auto',
      spaceBetween: 40,
      navigation: {
        nextEl: slider.querySelector('.included-items-nav-next'),
        prevEl: slider.querySelector('.included-items-nav-prev'),
      },
      breakpoints: {
        768: {
          slidesPerView: 'auto',
          spaceBetween: 40,
        },
      },
    });
  };

  const resetIncludedItemsDrawersForSwap = () => {
    document.querySelectorAll('x-drawer.included-items-drawer').forEach((drawer) => {
      if (drawer.hasAttribute('open')) {
        drawer.removeAttribute('open');
      }

      if (drawer.parentElement === document.body) {
        drawer.remove();
      }
    });
  };

  const initIncludedItemsDrawers = (root = document) => {
    root.querySelectorAll('.included-items-drawer').forEach((drawer) => {
      setIncludedItemsDrawerDirection(drawer);
    });
  };

  const initPdpColorSwiper = (root = document) => {
    if (typeof Swiper === 'undefined') return;

    root.querySelectorAll('[data-pdp-color-swatches]:not([data-pdp-swiper-init])').forEach((wrapper) => {
      wrapper.setAttribute('data-pdp-swiper-init', 'true');
      new Swiper(wrapper, {
        slidesPerView: 'auto',
        spaceBetween: 8,
        navigation: {
          nextEl: wrapper.querySelector('.product-info__variations-btn--next'),
          prevEl: wrapper.querySelector('.product-info__variations-btn--prev'),
        },
      });
    });
  };

  const getHandleFromPath = (pathname) => {
    const match = String(pathname).match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  };

  const formatBreadcrumbTitle = (title) => {
    if (!title) return '';
    return String(title).split('|')[0].trim();
  };

  const updateBreadcrumbsTitle = (title) => {
    const cleanTitle = formatBreadcrumbTitle(title);
    if (!cleanTitle) return;

    document.querySelectorAll('.shopify-section.breadcrumbs').forEach((section) => {
      const items = section.querySelectorAll('.breadcrumbs__list .breadcrumbs__item');
      for (let i = items.length - 1; i >= 0; i -= 1) {
        const item = items[i];
        if (item.tagName === 'SPAN' && !item.querySelector('a')) {
          item.textContent = cleanTitle;
          break;
        }
      }
    });
  };

  const getHandleFromQuickBuyAttr = (handleAttr) => {
    if (!handleAttr) return null;
    const path = handleAttr.startsWith('/') ? handleAttr : `/products/${handleAttr}`;
    return getHandleFromPath(path.split('?')[0]);
  };

  const getQuickBuyTemplate = (root) =>
    root?.querySelector('#quick-buy-content') || document.getElementById('quick-buy-content');

  const applyQuickBuyTemplateToDrawer = (drawer, template) => {
    if (!drawer || !template?.content) return;

    const fragment = template.content.cloneNode(true);
    fragment.querySelectorAll('noscript').forEach((node) => node.remove());
    drawer.replaceChildren(fragment);
    drawer._hasLoaded = true;

    if (typeof window.initQuickBuyGallery === 'function') {
      window.initQuickBuyGallery(drawer);
    }
    if (typeof window.initPdpColorSwiper === 'function') {
      window.initPdpColorSwiper(drawer);
    } else {
      initPdpColorSwiper(drawer);
    }

    const handle = getHandleFromQuickBuyAttr(drawer.getAttribute?.('handle'));
    if (typeof window.reinitPreorderForScope === 'function') {
      window.reinitPreorderForScope(drawer, handle);
    }

    if (typeof Shopify !== 'undefined' && typeof Shopify.PaymentButton?.init === 'function') {
      Shopify.PaymentButton.init();
    }
  };

  const buildQuickBuyHandle = (handle, variantId, viewSuffix) => {
    const view = viewSuffix || getViewSuffix();
    const params = new URLSearchParams();
    if (view) params.set('view', view);
    if (variantId) params.set('variant', variantId);
    const qs = params.toString();
    return qs ? `${handle}?${qs}` : handle;
  };

  const refreshQuickBuyDrawers = (root, productHandle) => {
    const template = getQuickBuyTemplate(root);
    const viewSuffix = getViewSuffix();
    const variantId = document.querySelector('form[is="product-form"] input[name="id"]')?.value;
    const swatchHandles = new Set(
      Array.from(document.querySelectorAll('[data-pdp-color-swatch]'))
        .map((el) => el.getAttribute('data-pdp-handle'))
        .filter(Boolean)
    );

    document.querySelectorAll('quick-buy-drawer').forEach((drawer) => {
      const drawerHandle = getHandleFromQuickBuyAttr(drawer.getAttribute('handle'));
      if (!drawerHandle) return;

      const inColorGroup = swatchHandles.has(drawerHandle);
      const isCurrentProduct = drawerHandle === productHandle;

      if (!inColorGroup && !isCurrentProduct) return;

      if (isCurrentProduct) {
        drawer.setAttribute('handle', buildQuickBuyHandle(productHandle, variantId, viewSuffix));
      } else if (viewSuffix) {
        drawer.setAttribute('handle', appendViewToHandleAttr(drawer.getAttribute('handle'), viewSuffix));
      }

      const isOpen = drawer.hasAttribute('open') || drawer.open;
      if (isOpen && isCurrentProduct && template) {
        applyQuickBuyTemplateToDrawer(drawer, template);
        return;
      }

      drawer._hasLoaded = false;
    });
  };

  const patchQuickBuyDrawer = () => {
    if (window.__pdpQuickBuyDrawerPatched) return;

    customElements.whenDefined('quick-buy-drawer').then(() => {
      if (window.__pdpQuickBuyDrawerPatched) return;

      const QuickBuyDrawer = customElements.get('quick-buy-drawer');
      if (!QuickBuyDrawer?.prototype?.show) return;

      const originalShow = QuickBuyDrawer.prototype.show;

      QuickBuyDrawer.prototype.show = async function patchedShow() {
        const drawerHandle = getHandleFromQuickBuyAttr(this.getAttribute('handle'));
        const currentHandle = getCurrentPdpHandle();
        const template = getQuickBuyTemplate();

        const runPostShowPreorder = () => {
          if (this._hasLoaded && typeof window.reinitPreorderForScope === 'function') {
            window.reinitPreorderForScope(this, drawerHandle);
          }
        };

        if (template && currentHandle && drawerHandle === currentHandle) {
          this.style.display = 'block';
          if (!this._hasLoaded) {
            [this, ...this.controls].forEach((control) => control.setAttribute('aria-busy', 'true'));
            applyQuickBuyTemplateToDrawer(this, template);
            [this, ...this.controls].forEach((control) => control.setAttribute('aria-busy', 'false'));
          }
          const result = await originalShow.call(this);
          runPostShowPreorder();
          return result;
        }

        const result = await originalShow.call(this);
        runPostShowPreorder();
        return result;
      };

      window.__pdpQuickBuyDrawerPatched = true;
    });
  };

  // Globo identifies the current product from window.ShopifyAnalytics.meta.product
  // (and window.meta.product), which Shopify only sets on a full page load and never
  // updates on AJAX. After a colour swap it still points at the original product, so
  // Globo looks up the wrong preorder profile and renders nothing. Refresh it from
  // the lightweight /products/<handle>.js endpoint for the swapped product.
  const refreshAnalyticsMetaProduct = async (handle) => {
    if (!handle) return;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${handle}.js`);
      if (!response.ok) return;

      const product = await response.json();
      const metaProduct = {
        id: product.id,
        gid: `gid://shopify/Product/${product.id}`,
        handle: product.handle,
        vendor: product.vendor,
        type: product.type,
        variants: (product.variants || []).map((variant) => ({
          id: variant.id,
          price: variant.price,
          name: variant.name,
          public_title: variant.public_title,
          sku: variant.sku,
        })),
      };

      if (window.ShopifyAnalytics?.meta) window.ShopifyAnalytics.meta.product = metaProduct;
      if (window.meta) window.meta.product = metaProduct;
    } catch (error) {
      console.warn('Failed to refresh analytics meta for preorder', error);
    }
  };

  const hexToRgbTriplet = (hex) => {
    if (!hex) return null;
    const match = String(hex).replace('#', '').match(/^([0-9a-f]{6})$/i);
    if (!match) return null;
    const value = parseInt(match[1], 16);
    return `${(value >> 16) & 255} ${(value >> 8) & 255} ${value & 255}`;
  };

  const getGloboLocaleKey = (translation) => {
    if (!translation) return null;
    const lang = (window.Shopify?.locale || document.documentElement.lang || 'default')
      .toLowerCase()
      .split('-')[0];
    return translation[lang] ? lang : 'default';
  };

  // Globo Pre-order cannot re-render after a client-side section swap (it binds to the
  // product at full page load). But its full config — button HTML, message and colours
  // per locale — is available in window.GloboPreorderParams, so for preorder products
  // we render a Globo-equivalent button ourselves from that config. We reuse Globo's own
  // classes (.gPreorderBtn / .gPreorderTopMessage), which Globo has already styled on the
  // page, so the look matches. Gate: the server only renders the preorder delivery-date
  // block (.product-info__custom-inventory--preorder) for preorder products, so its
  // presence in the swapped section is our signal — no guessing.
  const loadSecondarySections = async (
    productUrl,
    upgradeSection,
    featuredSection,
    includedItemsSection,
    upgradeSectionId,
    featuredSectionId,
    includedItemsSectionId
  ) => {
    const tasks = [];

    if (upgradeSection && upgradeSectionId) {
      tasks.push(
        fetchSectionHtmlCached(productUrl, upgradeSectionId, 'low').then((html) => ({
          type: 'upgrade',
          html,
        }))
      );
    }

    if (featuredSection && featuredSectionId) {
      tasks.push(
        fetchSectionHtmlCached(productUrl, featuredSectionId, 'low').then((html) => ({
          type: 'featured',
          html,
        }))
      );
    }

    if (includedItemsSection && includedItemsSectionId) {
      tasks.push(
        fetchSectionHtmlCached(productUrl, includedItemsSectionId, 'low').then((html) => ({
          type: 'included-items',
          html,
        }))
      );
    }

    if (!tasks.length) return;

    try {
      const results = await Promise.all(tasks);
      const upgradeResult = results.find((r) => r.type === 'upgrade');
      const featuredResult = results.find((r) => r.type === 'featured');
      const includedItemsResult = results.find((r) => r.type === 'included-items');

      const currentUpgrade = getEtUpgradeSection();
      if (currentUpgrade && upgradeResult) {
        const newUpgradeSection = parseSectionFromResponse(
          upgradeResult.html,
          currentUpgrade,
          upgradeSectionId
        );
        if (newUpgradeSection) {
          currentUpgrade.replaceWith(newUpgradeSection);
          if (typeof window.initEtUpgradeSection === 'function') {
            window.initEtUpgradeSection(newUpgradeSection);
          }
        }
      }

      const currentFeatured = getFeaturedCollectionSection();
      if (currentFeatured && featuredResult) {
        const newFeaturedSection = parseSectionFromResponse(
          featuredResult.html,
          currentFeatured,
          featuredSectionId
        );
        if (newFeaturedSection) {
          currentFeatured.replaceWith(newFeaturedSection);
          initFeaturedCollectionSwiper(newFeaturedSection);
        }
      }

      const currentIncludedItems = getIncludedItemsSection();
      if (currentIncludedItems && includedItemsResult) {
        const newIncludedItemsSection = parseSectionFromResponse(
          includedItemsResult.html,
          currentIncludedItems,
          includedItemsSectionId
        );
        if (newIncludedItemsSection) {
          currentIncludedItems.replaceWith(newIncludedItemsSection);
          initIncludedItemsSectionSlider(newIncludedItemsSection);
        }
      }
    } catch (error) {
      console.warn('Secondary section swap failed:', error);
    }
  };

  const applyGloboPreorderButton = (sectionEl) => {
    const dateBlock = sectionEl.querySelector('.product-info__custom-inventory--preorder');
    if (!dateBlock) return; // not a preorder product

    const params = window.GloboPreorderParams;
    const localeKey = getGloboLocaleKey(params?.translation);
    const strings = params?.translation?.[localeKey] || params?.translation?.default;
    if (!strings?.preorderText) return;

    const form = sectionEl.querySelector('form[is="product-form"]');
    if (!form) return;
    // If Globo (or a previous run) already placed a preorder button, sync UI only.
    if (form.querySelector('.gPreorderBtnLoaded, .ajaxPreorderBtn, .themePreorderBtn')) {
      if (typeof window.syncPreorderUi === 'function') {
        window.syncPreorderUi(form, sectionEl);
      }
      initPdpStickyBar(sectionEl);
      return;
    }

    const buyButtons = form.querySelector('buy-buttons');
    const atcBtn = buyButtons?.querySelector('button[type="submit"]');
    if (!buyButtons || !atcBtn) return;

    // Cart properties so the order is flagged as a preorder (same names Globo uses).
    const propName = params?.properties?.preorder || '_is_preorder';
    const localeProp = params?.properties?.locale || '_preorder_locale';
    if (!form.querySelector(`input[name="properties[${propName}]"]`)) {
      const isPreorderInput = document.createElement('input');
      isPreorderInput.type = 'hidden';
      isPreorderInput.name = `properties[${propName}]`;
      isPreorderInput.value = strings.preorderLabel || 'Pre order';
      isPreorderInput.className = 'ajaxPreorderProperty';
      form.appendChild(isPreorderInput);

      const localeInput = document.createElement('input');
      localeInput.type = 'hidden';
      localeInput.name = `properties[${localeProp}]`;
      localeInput.value = localeKey === 'default' ? window.Shopify?.locale || '' : localeKey;
      localeInput.className = 'ajaxPreorderProperty';
      form.appendChild(localeInput);
    }

    // Message above the button.
    if (strings.messageAboveText && !buyButtons.parentElement.querySelector('.ajaxPreorderMessage')) {
      const message = document.createElement('div');
      message.className = 'ajaxPreorderMessage gPreorderTopMessageParent';
      message.innerHTML = `<div class="gPreorderTopMessage">${strings.messageAboveText}</div>`;
      buyButtons.insertAdjacentElement('beforebegin', message);
    }

    // The PRE ORDER button itself — reuses Globo's classes for styling.
    const rgb = hexToRgbTriplet(params?.settings?.colorButton?.preorderBackgroundColor) || '234 114 33';
    const preorderBtn = document.createElement('button');
    preorderBtn.type = 'submit';
    preorderBtn.className = 'button button--xl ajaxPreorderBtn gPreorderBtn gPreorderBtnLoaded';
    preorderBtn.style.cssText = `--button-background: ${rgb} / var(--button-background-opacity, 1);--button-outline-color: ${rgb};`;
    preorderBtn.innerHTML = `<span>${strings.preorderText}</span>`;

    atcBtn.style.display = 'none';
    atcBtn.insertAdjacentElement('afterend', preorderBtn);

    // Reveal the server-rendered delivery-date block.
    dateBlock.classList.add('visible-preorder');
    dateBlock.style.display = 'flex';

    // Flag the form as processed so Globo (if its observer ever fires) won't add a
    // second button. Class name comes from Globo's own config.
    const loadedClass = params?.classes?.preorderLoadedForm || 'preorderLoaded';
    form.classList.add(loadedClass);

    if (typeof window.syncPreorderUi === 'function') {
      window.syncPreorderUi(form, sectionEl);
    }

    initPdpStickyBar(sectionEl);
  };

  const hasPreorderButton = (sectionEl) =>
    Boolean(
      sectionEl?.querySelector('.gPreorderBtnLoaded, .ajaxPreorderBtn, .themePreorderBtn')
    );

  // Globo binds at full page load only; after a set/colour swap we render manually.
  // On first paint GloboPreorderParams may still be loading, so retry briefly.
  // Do not call reinitPreorderForScope here — Globo re-init strips our button.
  const initMainProductPreorder = (sectionEl = getMainProductSection()) => {
    if (!sectionEl) return;
    if (!sectionEl.querySelector('.product-info__custom-inventory--preorder')) return;

    const form = sectionEl.querySelector('form[is="product-form"]');
    const apply = () => applyGloboPreorderButton(sectionEl);

    apply();

    let tries = 0;
    const maxTries = 24;
    const timer = setInterval(() => {
      tries += 1;
      if (!hasPreorderButton(sectionEl)) apply();
      if (tries >= maxTries) clearInterval(timer);
    }, 250);

    if (!form) return;

    const observer = new MutationObserver(() => {
      if (
        sectionEl.querySelector('.product-info__custom-inventory--preorder') &&
        !hasPreorderButton(sectionEl)
      ) {
        apply();
        if (typeof window.syncPreorderUi === 'function') {
          window.syncPreorderUi(form, sectionEl);
        }
      }
    });
    observer.observe(form, { childList: true, subtree: true });
  };

  const swapMainProductSection = async (productUrl, productHandle, productId = null) => {
    if (isLoading) return;

    const mainSection = getMainProductSection();
    if (!mainSection) return;

    const upgradeSection = getEtUpgradeSection();
    const featuredSection = getFeaturedCollectionSection();
    const includedItemsSection = getIncludedItemsSection();
    const mainSectionId = mainSection.id.replace('shopify-section-', '');
    const upgradeSectionId = upgradeSection?.id.replace('shopify-section-', '');
    const featuredSectionId = featuredSection?.id.replace('shopify-section-', '');
    const includedItemsSectionId = includedItemsSection?.id.replace('shopify-section-', '');
    const viewSuffix = getViewSuffix();
    const needsComplementary = hasComplementaryBlock(mainSection);

    isLoading = true;
    mainSection.classList.add('is-pdp-color-loading');

    try {
      const criticalTasks = [
        fetchSectionHtmlCached(productUrl, mainSectionId, 'high').then((html) => ({
          type: 'main',
          html,
        })),
      ];

      if (needsComplementary) {
        criticalTasks.push(
          (async () => {
            try {
              const resolvedProductId =
                productId || (await fetchProductIdByHandle(productHandle));
              if (!resolvedProductId) return { type: 'complementary', html: null };

              const html = await fetchComplementaryHtmlCached(
                resolvedProductId,
                mainSectionId,
                getComplementaryLimit(mainSection)
              );
              return { type: 'complementary', html };
            } catch (error) {
              console.warn('Complementary prefetch failed:', error);
              return { type: 'complementary', html: null };
            }
          })()
        );
      }

      const results = await Promise.all(criticalTasks);
      const mainResult = results.find((r) => r.type === 'main');

      const newMainSection = parseSectionFromResponse(mainResult.html, mainSection, mainSectionId);
      if (!newMainSection) throw new Error('Main product section not found in response');

      const complementaryLimit = getComplementaryLimit(mainSection, newMainSection);
      const complementaryResult = results.find((r) => r.type === 'complementary');
      let complementaryInjected = false;
      if (complementaryResult?.html) {
        const sourceEl = extractComplementaryMarkup(complementaryResult.html);
        complementaryInjected = injectComplementaryIntoSection(
          newMainSection,
          sourceEl,
          complementaryLimit
        );
      }

      resetIncludedItemsDrawersForSwap();
      // Relocate the included-items trigger while the section is still detached,
      // so it never paints in its source position after the swap.
      moveIncludedItemsIntoSetSelector(newMainSection);
      mainSection.replaceWith(newMainSection);

      const titleEl = newMainSection.querySelector('.product-info__title, h1.product-info__title');
      const newTitle = titleEl?.textContent?.trim();
      if (newTitle) {
        document.title = newTitle;
        updateBreadcrumbsTitle(newTitle);
      }

      const url = new URL(productUrl, window.location.origin);
      if (viewSuffix) url.searchParams.set('view', viewSuffix);
      window.history.pushState({ pdpColorSwap: true }, '', `${url.pathname}${url.search}`);

      const resolvedHandle = productHandle || getHandleFromPath(url.pathname);

      // Globo reads ShopifyAnalytics.meta.product on section:load. Refresh it first so a
      // swap back to an OOS colour is not treated as the previous preorder product.
      await refreshAnalyticsMetaProduct(resolvedHandle);

      initPdpColorSwiper(newMainSection);
      if (needsComplementary && !complementaryInjected) {
        reinitProductRecommendations(newMainSection);
      }

      isLoading = false;
      getMainProductSection()?.classList.remove('is-pdp-color-loading');

      requestAnimationFrame(() => {
        if (typeof window.initProductVideoSlider === 'function') {
          window.initProductVideoSlider(newMainSection);
        }

        newMainSection.dispatchEvent(
          new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: { sectionId: mainSectionId },
          })
        );

        if (newMainSection.querySelector('.product-info__custom-inventory--preorder')) {
          applyGloboPreorderButton(newMainSection);
        } else if (typeof window.guardNonPreorderSection === 'function') {
          window.guardNonPreorderSection(newMainSection);
        } else if (typeof window.resetInjectedPreorderUi === 'function') {
          window.resetInjectedPreorderUi(newMainSection);
          const form = newMainSection.querySelector('form[is="product-form"]');
          if (form && typeof window.syncPreorderUi === 'function') {
            window.syncPreorderUi(form, newMainSection);
          }
        }

        if (typeof Shopify !== 'undefined' && typeof Shopify.PaymentButton?.init === 'function') {
          Shopify.PaymentButton.init();
        }

        refreshQuickBuyDrawers(newMainSection, resolvedHandle);

        if (typeof window.cacheComplementaryPdpPrice === 'function') {
          window.cacheComplementaryPdpPrice();
        }

        initGratisPannenStickyCta(newMainSection);
        initIncludedItemsDrawers(newMainSection);
        initGalleryStickyOffset(newMainSection);
        moveIncludedItemsIntoSetSelector(newMainSection);
      });
      void loadSecondarySections(
        productUrl,
        upgradeSection,
        featuredSection,
        includedItemsSection,
        upgradeSectionId,
        featuredSectionId,
        includedItemsSectionId
      );
    } catch (error) {
      console.error('PDP color swap failed:', error);
      window.location.assign(productUrl);
    } finally {
      isLoading = false;
      getMainProductSection()?.classList.remove('is-pdp-color-loading');
    }
  };

  document.addEventListener('click', (event) => {
    const swatch = getPdpSwatch(event.target);
    if (!swatch) return;

    const drawer = swatch.closest('quick-buy-drawer');
    if (drawer) return;

    const root = getPdpSwapRoot(swatch);
    if (!root) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const handle = swatch.getAttribute('data-pdp-handle');
    const productUrl = swatch.getAttribute('data-pdp-url') || swatch.getAttribute('href');
    const productId = swatch.getAttribute('data-pdp-product-id');
    const currentHandle = root.getAttribute('data-current-handle');

    if (!handle || !productUrl || handle === currentHandle) return;

    swapMainProductSection(productUrl, handle, productId);
  });

  // Hover preview: show the swatch's colour name + its extra price in the colour
  // label, mirroring snippets/ecom-custom-color.liquid. Delegated so it keeps
  // working after a section swap (the snippet has no inline script of its own).
  document.addEventListener(
    'touchstart',
    (event) => {
      const swatch = getPdpSwatch(event.target);
      if (swatch) prefetchSwapData(swatch);
    },
    { passive: true, capture: true }
  );

  document.addEventListener('mouseover', (event) => {
    const swatch = getPdpSwatch(event.target);
    if (!swatch) return;

    prefetchSwapData(swatch);

    if (!swatch.hasAttribute('data-pdp-color-swatch')) return;

    const root = getPdpSwapRoot(swatch);
    const label = root?.querySelector('[data-pdp-color-label]');
    if (!label) return;

    if (label.dataset.colorLabelOriginal == null) {
      label.dataset.colorLabelOriginal = label.innerHTML;
    }

    const hoverName = swatch.getAttribute('data-hover-color-name');
    if (hoverName) label.innerHTML = hoverName;
  });

  document.addEventListener('mouseout', (event) => {
    if (!(event.target instanceof Element)) return;
    const list = event.target.closest('[data-pdp-color-swatches]');
    if (!list || list.contains(event.relatedTarget)) return; // still within the swatches

    const label = list.closest('[data-pdp-color-swap]')?.querySelector('[data-pdp-color-label]');
    if (label && label.dataset.colorLabelOriginal != null) {
      label.innerHTML = label.dataset.colorLabelOriginal;
      delete label.dataset.colorLabelOriginal;
    }
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const sectionItem = event.target.closest('.included-items-container .included-item');
    if (sectionItem) {
      const container = sectionItem.closest('.included-items-container');
      const index = sectionItem.getAttribute('data-index');
      if (!container || !index) return;

      container.querySelectorAll('.included-item').forEach((el) => el.classList.remove('selected'));
      container.querySelectorAll('.included-items-banner').forEach((el) => el.classList.remove('selected'));
      sectionItem.classList.add('selected');
      container.querySelector(`.included-items-banner[data-index="${index}"]`)?.classList.add('selected');
      return;
    }

    const drawerItem = event.target.closest('.included-items-drawer__body .included-item');
    if (drawerItem) {
      const body = drawerItem.closest('.included-items-drawer__body');
      if (!body) return;

      body.querySelectorAll('.included-item').forEach((el) => el.classList.remove('selected'));
      drawerItem.classList.add('selected');
    }
  });

  let includedDrawerResizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(includedDrawerResizeTimeout);
    includedDrawerResizeTimeout = setTimeout(() => {
      document.querySelectorAll('.included-items-drawer').forEach(setIncludedItemsDrawerDirection);
    }, 100);
  });

  // The gallery sticks below the header on desktop, but when it is taller
  // than the viewport that leaves its bottom unreachable until the product
  // info has scrolled past. The media column is sized in CSS so the gallery
  // fits at first paint; if it still does not fit, anchor it to the viewport
  // bottom (negative sticky top). This only moves the sticky offset, never
  // the layout, so it cannot shift the page.
  const GALLERY_STICKY_BOTTOM_GAP = 20;
  let galleryStickyWrapper = null;
  let galleryStickyObserver = null;
  let galleryStickyResizeBound = false;

  const updateGalleryStickyOffset = () => {
    const wrapper = galleryStickyWrapper;
    if (!wrapper || !wrapper.isConnected) return;
    wrapper.style.removeProperty('top');
    if (window.innerWidth < 1000) return;
    const defaultTop = parseFloat(window.getComputedStyle(wrapper).top) || 0;
    const bottomAnchoredTop = window.innerHeight - wrapper.offsetHeight - GALLERY_STICKY_BOTTOM_GAP;
    if (bottomAnchoredTop < defaultTop) {
      wrapper.style.top = `${bottomAnchoredTop}px`;
    }
  };

  const refreshGallerySizing = () => {
    updateGalleryStickyOffset();
  };

  const initGalleryStickyOffset = (scope) => {
    const root = scope || getMainProductSection() || document;
    // Scope to the new PDP (.product-new) — this script also loads on the
    // old main-product section, where the gallery must stay untouched.
    const wrapper = root.querySelector('.product-new .product-gallery-wrapper');
    if (!wrapper) return;
    galleryStickyWrapper = wrapper;
    galleryStickyObserver?.disconnect();
    galleryStickyObserver = new ResizeObserver(refreshGallerySizing);
    galleryStickyObserver.observe(wrapper);
    if (!galleryStickyResizeBound) {
      galleryStickyResizeBound = true;
      let galleryStickyResizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(galleryStickyResizeTimeout);
        galleryStickyResizeTimeout = setTimeout(refreshGallerySizing, 100);
      });
    }
    refreshGallerySizing();
  };

  // Move the "Wat zit er in de set?" trigger (included-items block) next to
  // the "Kies je set" label in the set-selector header. The drawer moves
  // along inside the same container, so its bindings keep working.
  const moveIncludedItemsIntoSetSelector = (scope) => {
    const root = scope || getMainProductSection() || document;
    const header = root.querySelector('.gratis-pannen-selector__header');
    const included = root.querySelector('.product-info__included-items');
    if (!header || !included || header.contains(included)) return;
    const blockItem = included.closest('[data-block-type="included-items"]');
    header.appendChild(included);
    if (blockItem && !blockItem.childElementCount) {
      blockItem.style.display = 'none';
    }
  };

  window.addEventListener('popstate', () => {
    if (!document.querySelector('[data-pdp-color-swap], [data-pdp-set-swap]')) return;

    const handle = getHandleFromPath(window.location.pathname);
    if (!handle) return;

    const currentHandle = getCurrentPdpHandle();
    if (handle === currentHandle) return;

    swapMainProductSection(`${window.location.pathname}${window.location.search}`, handle);
  });

  const init = () => {
    initPdpColorSwiper();
    initGratisPannenStickyCta();
    initIncludedItemsDrawers();
    initIncludedItemsSectionSlider(getIncludedItemsSection());
    patchQuickBuyDrawer();
    initMainProductPreorder();
    initGalleryStickyOffset();
    moveIncludedItemsIntoSetSelector();
  };

  window.getPdpViewSuffix = getViewSuffix;
  window.appendViewToHandleAttr = appendViewToHandleAttr;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
